import { useCallback, useSyncExternalStore } from "react";

export type OperationsNavigate = (destination: string) => void;

const NAVIGATION_EVENT = "das:operations:navigation";

function currentHref(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function subscribeToNavigation(onChange: () => void): () => void {
  let lastHref = currentHref();
  const changed = () => {
    lastHref = currentHref();
    onChange();
  };
  const popped = () => {
    if (currentHref() === lastHref) return;
    if (!window.dispatchEvent(new Event("das:operations:before-navigate", { cancelable: true }))) {
      window.history.pushState(null, "", lastHref);
      return;
    }
    changed();
  };
  window.addEventListener("popstate", popped);
  window.addEventListener(NAVIGATION_EVENT, changed);
  return () => {
    window.removeEventListener("popstate", popped);
    window.removeEventListener(NAVIGATION_EVENT, changed);
  };
}

export function useOperationsNavigation(): { path: string; navigate: OperationsNavigate } {
  const href = useSyncExternalStore(subscribeToNavigation, currentHref, () => "/");
  const navigate = useCallback<OperationsNavigate>((destination) => {
    const target = new URL(destination, window.location.origin);
    if (target.origin !== window.location.origin) {
      throw new Error("Operations navigation requires a same-origin destination");
    }
    const nextHref = `${target.pathname}${target.search}${target.hash}`;
    if (nextHref === currentHref()) return;
    if (!window.dispatchEvent(new Event("das:operations:before-navigate", { cancelable: true })))
      return;
    window.history.pushState(null, "", nextHref);
    window.dispatchEvent(new Event(NAVIGATION_EVENT));
    const main = document.getElementById("operations-main");
    if (main) main.scrollTop = 0;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  return { path: new URL(href, window.location.origin).pathname, navigate };
}

/** Update a newly saved editor's address without leaving or remounting that editor. */
export function replaceOperationsLocation(destination: string): void {
  const target = new URL(destination, window.location.origin);
  if (target.origin !== window.location.origin)
    throw new Error("Operations navigation requires a same-origin destination");
  window.history.replaceState(null, "", `${target.pathname}${target.search}${target.hash}`);
  window.dispatchEvent(new Event(NAVIGATION_EVENT));
}
